import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useBrand } from '../contexts/BrandContext';

export interface ServicePricing {
  id: string;
  category: string;
  client_type: string | null;
  tier_key: string;
  name: string;
  price: number;
  additional_price: number | null;
  description: string | null;
  show_description?: boolean;
  features: string[];
  payment_schedule: Array<{ percentage: number; description: string }>;
  key_deliverables: string[];
}

export interface AdditionalPricing {
  id: string;
  item_key: string;
  name: string;
  price: number;
  description?: string | null;
  features?: any;
}

export interface SupportPlan {
  id: string;
  tier_key: string;
  name: string;
  hours: number;
  price: number;
  description?: string;
  features?: Array<{
    title: string;
    subtitle: string;
    items: string[];
  }>;
  support_communication?: {
    response_time?: string;
    channels?: string[];
    strategy_calls?: string;
    extras?: string[];
  };
  turnaround_times?: {
    simple?: string;
    moderate?: string;
    complex?: string;
  };
  additional_info?: {
    websites_included?: string | number;
    additional_website_cost?: number;
    additional_hour_rate?: number;
    max_additional_hours?: string;
  };
}

export interface DiscountCode {
  id: string;
  code: string;
  type: 'percentage' | 'fixed' | 'conditional';
  value: number | null;
  base_value: number | null;
  bonus_value: number | null;
  applicable_to: 'website' | 'marketing' | 'both';
  applies_to_services: string[] | null;
  applies_to_products: string[] | null;
  requires_products: string[] | null;
  discount_behavior: 'standalone' | 'conditional';
  min_spend: number | null;
  max_discount: number | null;
  waive_setup: boolean;
  condition: string | null;
  active: boolean;
}

export function usePricing() {
  const { selectedBrand, dataRefreshKey } = useBrand();
  const [servicePricing, setServicePricing] = useState<ServicePricing[]>([]);
  const [additionalPricing, setAdditionalPricing] = useState<AdditionalPricing[]>([]);
  const [supportPlans, setSupportPlans] = useState<SupportPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedBrand) {
      loadPricing();
    }
  }, [selectedBrand, dataRefreshKey]);

  async function loadPricing() {
    if (!selectedBrand) return;

    try {
      setLoading(true);

      const [servicesRes, additionalRes, supportRes] = await Promise.all([
        supabase
          .from('service_pricing')
          .select('*')
          .eq('brand_id', selectedBrand.id),
        supabase
          .from('additional_pricing')
          .select('*')
          .eq('brand_id', selectedBrand.id),
        supabase
          .from('support_plans')
          .select('*')
          .eq('brand_id', selectedBrand.id)
          .order('price'),
      ]);

      if (servicesRes.error) throw servicesRes.error;
      if (additionalRes.error) throw additionalRes.error;
      if (supportRes.error) throw supportRes.error;

      setServicePricing(servicesRes.data || []);
      setAdditionalPricing(additionalRes.data || []);
      setSupportPlans(supportRes.data || []);
    } catch (error) {
      console.error('Error loading pricing:', error);
    } finally {
      setLoading(false);
    }
  }

  function getServicesByCategory(category: string, clientType?: string) {
    return servicePricing
      .filter(
        s => s.category === category && (!clientType || s.client_type === clientType || s.client_type === null)
      )
      .sort((a, b) => a.price - b.price);
  }

  function getAdditionalPrice(itemKey: string): number {
    return additionalPricing.find(p => p.item_key === itemKey)?.price || 0;
  }

  return {
    servicePricing,
    additionalPricing,
    supportPlans,
    loading,
    refreshPricing: loadPricing,
    getServicesByCategory,
    getAdditionalPrice,
  };
}

export function useDiscountCodes() {
  const { selectedBrand } = useBrand();
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedBrand) {
      loadDiscountCodes();
    }
  }, [selectedBrand]);

  async function loadDiscountCodes() {
    if (!selectedBrand) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('brand_id', selectedBrand.id)
        .eq('active', true);

      if (error) throw error;
      setDiscountCodes(data || []);
    } catch (error) {
      console.error('Error loading discount codes:', error);
    } finally {
      setLoading(false);
    }
  }

  async function validateCode(code: string, applicableTo: 'website' | 'marketing'): Promise<DiscountCode | null> {
    if (!selectedBrand) return null;

    try {
      const { data, error } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('brand_id', selectedBrand.id)
        .eq('code', code.toUpperCase())
        .eq('active', true)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      if (data.applicable_to === applicableTo || data.applicable_to === 'both') {
        return data;
      }

      return null;
    } catch (error) {
      console.error('Error validating discount code:', error);
      return null;
    }
  }

  // Maps database category names to breakdown keys used in calculator
  function mapCategoryToBreakdownKey(category: string): string {
    const mapping: Record<string, string> = {
      'ad_platforms': 'ppc',
      'seo_services': 'seo',
      'email_services': 'email',
      'cro_services': 'cro',
      'support_plans': 'support_plans',
      'website_packages': 'website_packages',
      'web_addons': 'web_addons',
    };
    return mapping[category] || category;
  }

  function canApplyDiscount(discount: DiscountCode, serviceCategory: string): boolean {
    if (!discount.applies_to_services || discount.applies_to_services.length === 0) {
      return true;
    }
    // Check both the original category and the mapped breakdown key
    const breakdownKey = mapCategoryToBreakdownKey(serviceCategory);
    return discount.applies_to_services.includes(serviceCategory) ||
           discount.applies_to_services.includes(breakdownKey);
  }

  function canApplyProductDiscount(
    discount: DiscountCode,
    productKey: string,
    selectedProducts: string[]
  ): boolean {
    if (!discount.applies_to_products || discount.applies_to_products.length === 0) {
      return false;
    }

    if (!discount.applies_to_products.includes(productKey)) {
      return false;
    }

    if (discount.requires_products && discount.requires_products.length > 0) {
      return discount.requires_products.every(req => selectedProducts.includes(req));
    }

    return true;
  }

  function calculateDiscountAmount(
    discount: DiscountCode,
    amount: number,
    serviceCategory?: string
  ): number {
    if (serviceCategory && !canApplyDiscount(discount, serviceCategory)) {
      return 0;
    }

    if (discount.min_spend && amount < discount.min_spend) {
      return 0;
    }

    let discountAmount = 0;

    if (discount.type === 'percentage' && discount.value) {
      discountAmount = amount * (discount.value / 100);
    } else if (discount.type === 'fixed' && discount.value) {
      discountAmount = discount.value;
    }

    if (discount.max_discount && discountAmount > discount.max_discount) {
      discountAmount = discount.max_discount;
    }

    return Math.min(discountAmount, amount);
  }

  function calculateProductDiscountAmount(
    discount: DiscountCode,
    productKey: string,
    productPrice: number,
    selectedProducts: string[]
  ): number {
    if (!canApplyProductDiscount(discount, productKey, selectedProducts)) {
      return 0;
    }

    if (discount.min_spend && productPrice < discount.min_spend) {
      return 0;
    }

    let discountAmount = 0;

    if (discount.type === 'percentage' && discount.value) {
      discountAmount = productPrice * (discount.value / 100);
    } else if (discount.type === 'fixed' && discount.value) {
      discountAmount = discount.value;
    }

    if (discount.max_discount && discountAmount > discount.max_discount) {
      discountAmount = discount.max_discount;
    }

    return Math.min(discountAmount, productPrice);
  }

  return {
    discountCodes,
    loading,
    refreshDiscountCodes: loadDiscountCodes,
    validateCode,
    canApplyDiscount,
    canApplyProductDiscount,
    calculateDiscountAmount,
    calculateProductDiscountAmount,
    mapCategoryToBreakdownKey,
  };
}
